import { types } from "./mutations";
import {
  EntityModelLayer,
  EntityPropertyLayer,
  layerType,
  StaticLayer,
} from "@/models/Layers.model";
import StaticEntity from "@/models/StaticEntity.model";
import notify from "@/lib/notifications";
import { cloneDeep } from "lodash";
import {
  SingleValueWidget,
  TimeWidget,
  GeoWidget,
  CategoricalWidget,
  MetricAggregation,
  MetricTerm,
  WidgetMetric,
  widgetType,
} from "@/models/Widget.model";
import { getStaticEntities } from "@/api/static";

export default {
  /**
   * Loads initial data to the store
   */
  loadInitialData: async ({ commit, dispatch }, payload) => {
    commit(types.SET_ENTITY_MODELS, payload.entityModels);
    commit(types.SET_ENTITY_PROPS, payload.entityProps);
    commit(types.SET_COMPUTED_LAYERS, payload.computedLayers);
    commit(types.SET_LAYERS, payload.layerRegistry);

    // set an initial active layer
    if (payload.entityProps.length > 0) {
      dispatch("LayerManager/setActiveLayer", payload.entityProps[0], {
        root: true,
      });
    }
  },

  /**
   * Registers a layer in the studio layer registry
   * @param {Object, Object} param0
   * @param {EntityModelLayer | ComputedLayer} layer
   */
  registerLayer: async ({ commit, state }, layer) => {
    if (state.layerRegistry && state.layerRegistry.length > 0) {
      commit(types.SET_LAYERS, [...state.layerRegistry, layer]);
    } else {
      commit(types.SET_LAYERS, [layer]);
    }
  },

  /**
   * Sets a given layer as an active layer
   */
  setActiveLayer: async ({ commit, state }, layer) => {
    const layerEntry = { id: layer.id, type: "primary" };
    if (!(state.activeLayers && state.activeLayers.length > 0)) {
      commit(types.SET_ACTIVE_LAYERS, [layerEntry]);
    } else {
      const activeLayers = cloneDeep(state.activeLayers);
      // remove the current primary layer
      let newActiveLayers = activeLayers.filter(
        item => item.type !== "primary"
      );
      newActiveLayers =
        newActiveLayers && newActiveLayers.length > 0
          ? [...newActiveLayers, layerEntry]
          : [layerEntry];
      // TODO: When we activate selection of multiple layers, SET_ACTIVE_LAYERS with newActiveLayers
      commit(types.SET_ACTIVE_LAYERS, [layerEntry]);
    }
  },

  /**
   * Sets a different layer from the entity props list as an active layer
   */
  changeActiveLayer: async ({ dispatch, state }) => {
    dispatch("Map/loadGeoData", null, { root: true });
    dispatch("TimeManager/loadTimeData", null, { root: true });

    if (state.entityProps && state.entityProps.length > 0) {
      dispatch("LayerManager/setActiveLayer", state.entityProps[0], {
        root: true,
      });
    }
  },

  /**
   * Returns a Widget object for the current selected layer
   * For Categorical widger
   */
  createPayloadForCategorical: async ({ state, dispatch }) => {
    const activeLayer = state.activeLayers.find(
      item => item.type === "primary"
    );
    if (!activeLayer) return null;
    const layer = state.entityProps.find(item => item.id == activeLayer.id);
    if (!layer) return null; // layer is not an entity prop
    const model = state.entityModels.find(
      item => item.id === layer.entity_model_id
    );
    const meta = {
      id: model.id,
      column: null,
      type: layerType.CATEGORICAL,
    };

    let filters = await dispatch(
      "FilterManager/getAppliedEntityFilterPayload",
      model.id,
      { root: true }
    );
    // Add city filter only during edit modes retrieve the applied city filter
    let cityFilter = await dispatch("Map/getCityFilterPayload", null, {
      root: true,
    });
    if (cityFilter) {
      filters.push(cityFilter);
    }

    if (model.isEditable && layer.aggregation && layer.property) {
      const aggregation = new MetricAggregation(
        layer.aggregation.id,
        layer.property.id
      );
      const term = new MetricTerm(
        "A",
        model.entity_id,
        filters ? filters : [],
        model.entity_type,
        aggregation
      );
      const metric = new WidgetMetric([term], "A");
      const categoricalWidget = new CategoricalWidget(meta, metric, null);
      return categoricalWidget;
    } else {
      return {
        meta,
        metric: layer.id,
        applied_filters: filters ? filters : [],
        widget_type: widgetType.CATEGORICAL,
        col: null,
        setCols: true,
      };
    }
  },
  /**
   * Create the Metric object base don the active layer
   * @param {*} param0
   */
  createCurrentMetricPayload: async ({ state, dispatch }) => {
    const activeLayer = state.activeLayers.find(
      item => item.type === "primary"
    );
    if (!activeLayer) return Promise.reject("No layer found");

    let layer = null;
    layer = state.computedLayers.find(item => item.id === activeLayer.id);
    if (!layer) {
      layer = state.entityProps.find(item => item.id == activeLayer.id);
    }

    if (layer.type === layerType.COMPUTED_PROP) {
      if (!layer) return Promise.reject("No layer found"); // layer is not an entity prop
      if (layer.isEditable) {
        let terms = [];

        for (let j = 0; j < computedLayer.variables.length; j++) {
          let propId = computedLayer.variables[j];
          let index = j;

          // get the property data and the entity model data
          const prop = state.entityProps.find(item => item.id === propId);
          const entityModel = state.entityModels.find(
            item => item.id === prop.entity_model_id
          );
          const agg = new MetricAggregation(
            prop.aggregation.id,
            prop.property.id
          );
          const name = String.fromCharCode(index + 65); // get the expression [Alphabet] variable name

          let filters = null;

          if (save) {
            // get the locked filters
            filters = await dispatch(
              "FilterManager/getLockedEntityFilters",
              entityModel.id,
              { root: true }
            );
          } else {
            filters = await dispatch(
              "FilterManager/getAppliedEntityFilterPayload",
              entityModel.id,
              { root: true }
            );
            let cityFilter = await dispatch("Map/getCityFilterPayload", null, {
              root: true,
            });
            if (cityFilter) {
              filters.push(cityFilter);
            }
          }

          const term = new MetricTerm(
            name,
            entityModel.entity_id,
            filters ? filters : [],
            entityModel.entity_type,
            agg
          );
          terms.push(term);
        }

        const metric = new WidgetMetric(terms, computedLayer.expression);
        return { metric };
      } else {
        let cityFilter = await dispatch("Map/getCityFilterPayload", null, {
          root: true,
        });

        return cityFilter
          ? {
              metric: layer.id,
              applied_filters: [cityFilter],
            }
          : { metric: layer.id };
      }
    } else if (layer.type === layerType.ENTITY_PROP) {
      if (!layer) return Promise.reject("No layer found ");

      const model = state.entityModels.find(
        item => item.id === layer.entity_model_id
      );
      let filters = await dispatch(
        "FilterManager/getAppliedEntityFilterPayload",
        model.id,
        { root: true }
      );
      // Add city filter only during edit modes retrieve the applied city filter
      let cityFilter = await dispatch("Map/getCityFilterPayload", null, {
        root: true,
      });
      if (cityFilter) {
        filters.push(cityFilter);
      }

      if (model.isEditable) {
        const aggregation = new MetricAggregation(
          layer.aggregation.id,
          layer.property.id
        );
        const term = new MetricTerm(
          "A",
          model.entity_id,
          filters ? filters : [],
          model.entity_type,
          aggregation
        );
        const metric = new WidgetMetric([term], "A");
        return { metric };
      } else {
        // Add city filter only during edit modes retrieve the applied city filter
        let cityFilter = await dispatch("Map/getCityFilterPayload", null, {
          root: true,
        });
        return cityFilter
          ? {
              metric: layer.id,
              applied_filters: [cityFilter],
            }
          : { metric: layer.id };
      }
    }
  },

  /**
   * Generates the payload specified by the API to fetch data
   */
  createPayload: async (
    { state, dispatch },
    { event, active = false, save = false }
  ) => {
    const widgets = [];
    const activeLayers = state.activeLayers.map(item => item.id);

    let activeLayerIsProp = false,
      activeEntityLayer;

    if (activeLayers && activeLayers.length > 0) {
      activeEntityLayer = state.entityProps.find(
        item => item.id === activeLayers[0]
      );
      activeLayerIsProp = !!activeEntityLayer;
    }

    if (state.layerRegistry && state.layerRegistry.length > 0) {
      // denormalize all the layers in the layerRegistry
      for (let k = 0; k < state.layerRegistry.length; k++) {
        const layerItem = state.layerRegistry[k];
        // if the registered layer is an entity model....
        if (layerItem.type === layerType.ENTITY_MODEL) {
          let entityModel = state.entityModels.find(
            model => model.id === layerItem.id
          );
          const entityProps = state.entityProps.filter(
            item => item.entity_model_id === entityModel.id
          );

          for (let i = 0; i < entityProps.length; i++) {
            const prop = entityProps[i];
            const meta = {
              id: prop.id,
              name: prop.name,
              type: layerType.ENTITY_PROP,
            };

            // check if the property contains all it's values or is a non editable item
            if (
              !(
                prop.name &&
                prop.property &&
                prop.aggregation &&
                prop.isEditable
              )
            ) {
              if (prop.isEditable) {
                return;
              }
            }

            let filters = [];
            if (save) {
              // get the locked filters
              filters = await dispatch(
                "FilterManager/getAppliedEntityFilterPayload",
                entityModel.id,
                { root: true }
              );
            } else {
              filters = await dispatch(
                "FilterManager/getAppliedEntityFilterPayload",
                entityModel.id,
                { root: true }
              );
              // retrieve the applied city filter
              let cityFilter = await dispatch(
                "Map/getCityFilterPayload",
                null,
                { root: true }
              );
              if (cityFilter) {
                filters.push(cityFilter);
              }
            }

            if (prop.isEditable) {
              const terms = [
                new MetricTerm(
                  "A",
                  entityModel.entity_id,
                  filters ? filters : [],
                  entityModel.entity_type,
                  new MetricAggregation(prop.aggregation.id, prop.property.id)
                ),
              ];

              const expression = "A";
              const metric = new WidgetMetric(terms, expression);

              if (save) {
                const selectedFilters = await dispatch(
                  "FilterManager/getSelectedEntityFilters",
                  entityModel.id,
                  { root: true }
                );
                widgets.push({
                  name: prop.name,
                  desc: entityModel.name,
                  cohort_id: entityModel.id,
                  cohort_name: entityModel.name,
                  ...metric.toJSON(),
                  saved_filters: selectedFilters ? selectedFilters : [],
                });
                continue;
              }
              // if the prop is an active layer, include the time and geo widget
              else if (
                activeLayers.includes(prop.id) &&
                prop.entity_model_id === entityModel.id
              ) {
                const singleValue = new SingleValueWidget(meta, metric);
                widgets.push(singleValue.toJSON());
                const geoWidget = new GeoWidget(meta, metric);
                widgets.push(geoWidget.toJSON());

                // donot request for time data if the request is due to time slice change
                if (event !== "time_slice") {
                  const timeWidget = new TimeWidget(meta, metric);
                  widgets.push(timeWidget.toJSON());
                }
              } else if (
                activeLayerIsProp &&
                prop.entity_model_id === activeEntityLayer.entity_model_id
              ) {
                // Select entity prop layers present in the same cohort
                const singleValue = new SingleValueWidget(meta, metric);
                widgets.push(singleValue.toJSON());
              } else if (!active) {
                // donot include non active layers if active flag is set
                const singleValue = new SingleValueWidget(meta, metric);
                widgets.push(singleValue.toJSON());
              }
            } else {
              // saved metric, request by it's metric id
              const widget = {
                metric: prop.id,
                applied_filters: filters,
                meta,
              };

              if (
                activeLayers.includes(prop.id) &&
                prop.entity_model_id === entityModel.id
              ) {
                widgets.push(
                  {
                    ...widget,
                    widget_type: widgetType.SINGLE,
                  },
                  {
                    ...widget,
                    widget_type: widgetType.GEO,
                  }
                );

                // donot request for time data if the request is due to time slice change
                if (event !== "time_slice") {
                  widgets.push({
                    ...widget,
                    widget_type: widgetType.TIME,
                  });
                }
              } else if (!active) {
                // donot include non active layers if active flag is set
                widgets.push({
                  ...widget,
                  widget_type: widgetType.SINGLE,
                });
              }
            }
          }
        } else if (layerItem.type === layerType.COMPUTED_PROP) {
          // Get the computed layer from the list
          let computedLayer = state.computedLayers.find(
            layer => layer.id === layerItem.id
          );
          if (!computedLayer) return;

          const meta = {
            id: computedLayer.id,
            name: computedLayer.name,
            type: layerType.COMPUTED_PROP,
          };

          if (computedLayer.isEditable) {
            let terms = [];

            for (let j = 0; j < computedLayer.variables.length; j++) {
              let propId = computedLayer.variables[j];
              let index = j;

              // get the property data and the entity model data
              const prop = state.entityProps.find(item => item.id === propId);
              const entityModel = state.entityModels.find(
                item => item.id === prop.entity_model_id
              );
              const agg = new MetricAggregation(
                prop.aggregation.id,
                prop.property.id
              );
              const name = String.fromCharCode(index + 65); // get the expression [Alphabet] variable name

              let filters = null;

              if (save) {
                // get the locked filters
                filters = await dispatch(
                  "FilterManager/getLockedEntityFilters",
                  entityModel.id,
                  { root: true }
                );
              } else {
                filters = await dispatch(
                  "FilterManager/getAppliedEntityFilterPayload",
                  entityModel.id,
                  { root: true }
                );
                let cityFilter = await dispatch(
                  "Map/getCityFilterPayload",
                  null,
                  { root: true }
                );
                if (cityFilter) {
                  filters.push(cityFilter);
                }
              }

              const term = new MetricTerm(
                name,
                entityModel.entity_id,
                filters ? filters : [],
                entityModel.entity_type,
                agg
              );
              terms.push(term);
            }

            const metric = new WidgetMetric(terms, computedLayer.expression);

            // if the widget is created for saving purposes
            if (save) {
              widgets.push({
                name: computedLayer.name,
                desc: computedLayer.description,
                ...metric.toJSON(),
              });
            }
            // if the prop is an active layer, include the time and geo widget
            else if (activeLayers.includes(computedLayer.id)) {
              const singleValue = new SingleValueWidget(meta, metric);
              const geoWidget = new GeoWidget(meta, metric);
              widgets.push(singleValue.toJSON(), geoWidget.toJSON());
              // donot request for time data if the request is due to time slice change
              if (event !== "time_slice") {
                const timeWidget = new TimeWidget(meta, metric);
                widgets.push(timeWidget.toJSON());
              }
            } else if (!active) {
              // donot include non active layers if active flag is set
              const singleValue = new SingleValueWidget(meta, metric);
              widgets.push(singleValue.toJSON());
            }
          } else {
            let cityFilter = await dispatch("Map/getCityFilterPayload", null, {
              root: true,
            });

            const widget = cityFilter
              ? {
                  metric: computedLayer.id,
                  meta,
                  applied_filters: [cityFilter],
                }
              : { metric: computedLayer.id, meta };

            if (activeLayers.includes(computedLayer.id)) {
              widgets.push(
                {
                  ...widget,
                  widget_type: widgetType.SINGLE,
                },
                {
                  ...widget,
                  widget_type: widgetType.GEO,
                }
              );

              // donot request for time data if the request is due to time slice change
              if (event !== "time_slice") {
                widgets.push({
                  ...widget,
                  widget_type: widgetType.TIME,
                });
              }
            } else if (!active) {
              // donot include non active layers if active flag is set
              widgets.push({
                ...widget,
                widget_type: widgetType.SINGLE,
              });
            }
          }
        }
      }
      return widgets;
    } else {
      return null;
    }
  },

  /**
   * Loads the single value data from a reponse payload
   * Checks each widget Data's meta and mutates the objects based on the Id of the layers
   * @param {*} param0
   * @param {*} widgetData
   */
  loadSingleValues: async ({ dispatch }, widgetData) => {
    // check if the data is valid or not empty
    if (!(widgetData && widgetData.length > 0)) return;

    // pick only single value widgets
    widgetData
      .filter(widget => widget.type === widgetType.SINGLE)
      .forEach(widget => {
        if (widget.meta && widget.meta.id && widget.meta.type) {
          dispatch(
            "LayerManager/updateLayer",
            {
              ...widget.meta,
              value: widget.data,
            },
            { root: true }
          );
        }
      });
  },

  /**
   * Action to update layer based on a new duplicate object
   * All the property of the existing layer will be updated by the passsed object
   * @param {Object} param0
   * @param {Object} layer
   */
  updateLayer: async ({ commit, state }, layer) => {
    let layers = null;
    let layerToUpdate = null;

    switch (layer.type) {
      case layerType.ENTITY_PROP:
        // take the copy of the particular layer type
        layers = cloneDeep(state.entityProps);
        // find the layer to update
        layerToUpdate = layers.find(item => item.id === layer.id);
        // assign all the keys passed in the duplicate to actual layer
        Object.keys(layer).forEach(key => {
          layerToUpdate[key] = layer[key];
        });
        commit(types.SET_ENTITY_PROPS, layers);
        break;

      case layerType.ENTITY_MODEL:
        // take the copy of the particular layer type
        layers = cloneDeep(state.entityModels);
        // find the layer to update
        layerToUpdate = layers.find(item => item.id === layer.id);
        // assign all the keys passed in the duplicate to actual layer
        Object.keys(layer).forEach(key => {
          layerToUpdate[key] = layer[key];
        });
        commit(types.SET_ENTITY_MODELS, layers);
        break;

      case layerType.COMPUTED_PROP:
        // take the copy of the particular layer type
        layers = cloneDeep(state.computedLayers);
        // find the layer to update
        layerToUpdate = layers.find(item => item.id === layer.id);
        // assign all the keys passed in the duplicate to actual layer
        Object.keys(layer).forEach(key => {
          layerToUpdate[key] = layer[key];
        });
        commit(types.SET_COMPUTED_LAYERS, layers);
        break;
    }
  },

  /**
   * Adds a new entity property layer
   * @param {object, Object} param0
   * @param {EntityPropertyLayer} entityProp
   */
  addEntityProp: async ({ commit, state }, entityProp) => {
    // check if the entity prop already exist in the list
    if (state.entityProps.find(item => item.id === entityProp.id)) {
      // warn the user for duplicate layers
      notify.warn(
        "Layer Already Exists",
        "Another layer with the same property exists in the list"
      );
      return;
    }

    // add the prop to the list
    if (state.entityProps && state.entityProps.length > 0) {
      commit(types.SET_ENTITY_PROPS, [...state.entityProps, entityProp]);
    } else {
      commit(types.SET_ENTITY_PROPS, [entityProp]);
    }
    // TODO: Set the prop as activelayer
  },

  /**
   * Adds a new Entity Model layer in to the list and registers it
   * @param {Object, Object} param0
   * @param {EntityModelLayer} entityModel
   */
  addEntityModel: async ({ commit, state, dispatch }, entityModel) => {
    if (state.entityModels && state.entityModels.length > 0) {
      commit(types.SET_ENTITY_MODELS, [...state.entityModels, entityModel]);
    } else {
      commit(types.SET_ENTITY_MODELS, [entityModel]);
    }

    // register the layer
    dispatch(
      "LayerManager/registerLayer",
      {
        id: entityModel.id,
        type: entityModel.type,
      },
      { root: true }
    );
  },

  /**
   * Adds a new computed layer and registers the layer
   * @param {*} param0
   * @param {ComputerLayer} computedLayer
   */
  addComputedProp: async ({ commit, state, dispatch }, computedLayer) => {
    if (state.computedLayers && state.computedLayers.length > 0) {
      commit(types.SET_COMPUTED_LAYERS, [
        ...state.computedLayers,
        computedLayer,
      ]);
    } else {
      commit(types.SET_COMPUTED_LAYERS, [computedLayer]);
    }
    // register the layer
    dispatch(
      "LayerManager/registerLayer",
      {
        id: computedLayer.id,
        type: computedLayer.type,
      },
      { root: true }
    );
    // set the computed layer as the active layer
    dispatch("LayerManager/setActiveLayer", computedLayer, { root: true });
  },

  /**
   * Deletes a particular entity model and associated properties
   * @param {*} param0
   * @param {*} layer
   */
  deleteEntityModel: async ({ state, commit, dispatch }, layer) => {
    //remove all the filters associated with the entity model
    dispatch("FilterManager/removeFiltersForEntityModel", layer.id, {
      root: true,
    });
    // remove the layer from registry
    commit(
      types.SET_LAYERS,
      state.layerRegistry.filter(item => item.id !== layer.id)
    );
    // remove all the associated entity props layer
    state.entityProps
      .filter(item => item.entity_model_id === layer.id)
      .forEach(item =>
        dispatch("LayerManager/deleteEntityProp", item, { root: true })
      );
    // finally remove the layer from models
    commit(
      types.SET_ENTITY_MODELS,
      state.entityModels.filter(item => item.id !== layer.id)
    );
  },

  /**
   * Deletes an Entity Prop and all the associated computed properties
   * @param {*} param0
   * @param {*} layer
   */
  deleteEntityProp: async ({ state, commit, dispatch }, layer) => {
    // check if there exists type and id of the layer
    if (!layer.type || !layer.id) return;

    // remove all the computed layers associated with the entity property
    state.computedLayers
      .filter(item => item.variables && item.variables.includes(layer.id))
      .map(layer =>
        dispatch("LayerManager/deleteComputedProp", layer, { root: true })
      );
    // Remove the property from the list
    commit(
      types.SET_ENTITY_PROPS,
      state.entityProps.filter(item => item.id !== layer.id)
    );
  },

  /**
   * Deletes the entity property and removes it from the registry
   * @param {*} param0
   * @param {*} layer
   */
  deleteComputedProp: async ({ state, commit }, layer) => {
    if (!layer.type || !layer.id) return;
    // remove the layer from registry
    commit(
      types.SET_LAYERS,
      state.layerRegistry.filter(item => item.id !== layer.id)
    );
    // remove the layer from computed layers
    commit(
      types.SET_COMPUTED_LAYERS,
      state.computedLayers.filter(item => item.id !== layer.id)
    );
  },
  /**
   *
   * @param {Object} param0
   * @param {EntityModelLayer | EntityPropertyLayer | ComputedLayer} layer
   */
  deleteLayer: async ({ state, dispatch }, layer) => {
    // check if there exists type and id of the layer
    if (!layer.type || !layer.id) return;

    switch (layer.type) {
      case layerType.ENTITY_MODEL:
        dispatch("LayerManager/deleteEntityModel", layer, { root: true });
        break;
      case layerType.ENTITY_PROP:
        dispatch("LayerManager/deleteEntityProp", layer, { root: true });
        break;
      case layerType.COMPUTED_PROP:
        dispatch("LayerManager/deleteComputedProp", layer, { root: true });
        break;
      default:
        return;
    }

    if (state.activeLayers.find(item => item.id === layer.id)) {
      dispatch("LayerManager/changeActiveLayer", null, { root: true });
    }
  },

  // Only for bounce
  // TODO: Make it general and remove it once in for all
  loadStaticEntity: async ({ state, commit, dispatch }, entity) => {
    try {
      const response = await getStaticEntities({ entity_id: entity.id });
      const { status, data } = response;
      if (status === 200 && data) {
        const { data: entityList } = data;
        const staticLayer = new StaticLayer({
          id: entity.id,
          name: entity.name,
          entities: entityList.map(item => new StaticEntity(item)),
        });
        commit(types.SET_STATIC_LAYERS, [staticLayer]);
        dispatch("LayerManager/toggleStaticLayer", entity.id, {
          root: true,
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  },

  // toggles a static layer
  toggleStaticLayer: async ({ state, commit }, layerId) => {
    if (!state.activeStaticLayers.includes(layerId)) {
      commit(types.SET_ACTIVE_STATIC_LAYERS, [
        ...state.activeStaticLayers,
        layerId,
      ]);
    } else {
      commit(
        types.SET_ACTIVE_STATIC_LAYERS,
        state.activeStaticLayers.filter(id => id !== layerId)
      );
    }
  },

  /**
   * Resets the module state
   */
  reset: async ({ commit }) => {
    commit(types.reset);
  },
};
